import React from "react";
import styles from "./Form.module.css";
import IFormProps from "./dto/IFormProps";
import FormState from "./dto/FormState";
import { ColorClassNames, DefaultButton, FontClassNames, Icon, Label, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize, TextField } from "@fluentui/react";
import FormServiceFactory from "../../services/Form/FormServiceFactory";
import FormConstants from "../../dto/Form/FormConstants";
import FormSection from "../../dto/Form/FormSection";
import FormField from "../../dto/Form/FormField";
import FormFieldType from "../../dto/Form/FormFieldType";
import FormSaveData from "../../dto/Form/FormSaveData";
import MultiSelect from "./components/MultiSelect/MultiSelect";
import CaptchaServiceFactory from "../../services/Captcha/CaptchaServiceFactory";
import CaptchaData from "../../dto/Captcha/CaptchaData";
import FormTreeView from "./components/TreeView/FormTreeView";
import TableSelect from "./components/TableSelect/TableSelect";

export default class Form extends React.Component<IFormProps, FormState> {
  private captchaData: CaptchaData  | null = null;

  constructor(props: IFormProps) {
    super(props);
    this.state = new FormState();
  }

  public componentDidMount(): void {
    this.loadForm();
    this.initializeCaptcha();
  }

  private loadForm = async (): Promise<void> => {
    try {
      this.setState({
        isLoading: true
      });

      const urlParams = new URLSearchParams(window.location.search);
      let token = urlParams.get("token") || "";
      let formId = FormConstants.SignUpFormId;
      if(token.startsWith("pll_")) {
        formId = FormConstants.LongListFormId;
      }
      let data = await FormServiceFactory.getFormService().getFormData(formId, token);

      document.title = data.title;
      this.setState({
        isLoading: false,
        formData: data,
        saveData: new FormSaveData(),
        currentFormStep: 0
      })
    } catch (e) {
      console.error(e);
      this.setState({
        isLoading: false,
        errorOccured: true
      });
    }
  }

  private initializeCaptcha = async (): Promise<void> => {
    try {
      this.captchaData = await CaptchaServiceFactory.getCaptchaService().getCaptchaData();
      this.setState({
        captchaImageUrl: this.captchaData.dntCaptchaImgUrl,
        captchaValue: ""
      })
    } catch(e) {
      console.error(e);
      this.setState({
        isLoading: false,
        errorOccured: true
      });
    }
  }

  public render() {
    return (
      <div className={styles.formContainer}>
        {this.state.isLoading && <Spinner size={SpinnerSize.large}></Spinner>}
        {this.state.errorOccured && <div className={`${ColorClassNames.red} ${FontClassNames.xxLarge}`}><Icon iconName="ErrorBadge"></Icon></div>}
        {this.state.saveError && <MessageBar messageBarType={MessageBarType.error} onDismiss={this.dismissSaveError}>Fehler beim Speichern</MessageBar>}
        {this.state.formData && !this.state.isLoading && !this.state.errorOccured && !this.state.formWasSent && this.renderForm()}
        {this.state.formWasSent && <MessageBar messageBarType={MessageBarType.success}>Formular wurde erfolgreich gesendet.</MessageBar>}
      </div>
    );
  }

  private renderForm = (): JSX.Element | null => {
    if (!this.state.formData) {
      return null;
    }

    let sections = this.state.formData.sections;

    return (
      <div>
        <div className={styles.formHeaderContainer}>
          <h1 className={styles.formHeader}>{this.state.formData.title}</h1>
          {this.state.formData.subText && <p dangerouslySetInnerHTML={{ __html: this.state.formData.subText }}></p>}
        </div>
        {sections.length > 1 && <div className={styles.breadcrumb}>
          {sections.map((section, i) => <><div key={section.id} className={this.state.currentFormStep === i ? styles.selectedBreadcrumb : ""}>{section.title}</div>{i < sections.length - 1 && <div key={`sep_${section.id}`}>-</div>}</>)}
        </div>}
        <div className={styles.mainContent}>
          {sections.map((section, i) => this.renderSection(section, i === this.state.currentFormStep, i === sections.length - 1))}
        </div>
      </div>
    )
  }

  private renderSection = (section: FormSection, isVisible: boolean, isLast: boolean): JSX.Element => {
    return (
      <section className={`${isVisible ? "" : styles.hidden} ${styles.sectionContent}`} key={section.id}>
        <div className={styles.sectionHeadContainer}>
          <h2>{section.title}</h2>
          {section.subText && <p>{section.subText}</p>}
        </div>
        <div className={styles.innerContent}>
          <div>
            {section.fields.map(field => this.renderField(field))}
          </div>
          {isLast && this.state.formData?.needsCaptcha && <div>
            <Label>Bitte geben Sie hier den Sicherheitscode ein:</Label>
            <div className={styles.captchaContainer}>
              <div>
                {this.state.captchaImageUrl && <img src={this.state.captchaImageUrl} alt="Captcha"></img>}
              </div>
              <div>
                =
              </div>
              <div>
                <TextField value={this.state.captchaValue} onChange={this.onCaptchaValue} errorMessage={this.state.shouldValidate && !this.state.captchaValue ? "Bitte geben Sie den Sicherheitscode ein" : undefined}></TextField>
              </div>
              <div>
                <DefaultButton onClick={this.initializeCaptcha}>Neu generieren</DefaultButton>
              </div>
            </div>
          </div>}
        </div>
        <div className={styles.buttonContainer}>
          {this.state.shouldValidate && !this.validateRequiredField() && <span className={`${ColorClassNames.red} ${FontClassNames.small} ${styles.buttonError}`}>Bitte füllen Sie alle Pflichtfelder aus.</span>}
          {this.state.currentFormStep > 0 && <DefaultButton onClick={this.previousSection}>Zurück</DefaultButton>}
          {!isLast && <DefaultButton onClick={this.nextSection} className={styles.nextButton}>Weiter</DefaultButton>}
          {isLast && <PrimaryButton onClick={() => this.onSave()} className={styles.nextButton}>Absenden</PrimaryButton>}
        </div>
      </section>
    )
  }

  private previousSection = (): void => {
    this.setState({
      currentFormStep: this.state.currentFormStep - 1,
      shouldValidate: false,
      saveError: false
    });
  }

  private nextSection = (): void => {
    if (!this.validateRequiredField()) {
      this.setState({
        shouldValidate: true,
        saveError: false
      });
      return;
    }

    this.setState({
      currentFormStep: this.state.currentFormStep + 1,
      shouldValidate: false,
      saveError: false
    });
  }

  private renderField = (field: FormField): JSX.Element => {
    if(field.hide) {
      return <></>;
    }

    let content: JSX.Element;
    switch (field.type) {
      case FormFieldType.Text:
      case FormFieldType.Number:
        content = <TextField label={field.title} aria-label={field.title} required={field.required} type={field.type === FormFieldType.Number ? "number" : "text"} value={this.state.saveData.formFieldValues[field.id]}
          onChange={(_ev, val) => this.onTextFieldValueChanged(field, val)} errorMessage={this.getMandatoryMessage(field)}></TextField>;
        break;
      case FormFieldType.MultiSelect:
        content = <MultiSelect field={field} onChange={(val) => this.onTextFieldValueChanged(field, val)} selectedValues={this.state.saveData.formFieldValues[field.id]}
          errorMessage={this.getMandatoryMessage(field)}></MultiSelect>;
        break;
      case FormFieldType.Treeview:
        content = <FormTreeView field={field} selectedValues={this.state.saveData.formFieldValues[field.id]} onChange={(val) => this.onTextFieldValueChanged(field, val)}
          errorMessage={this.getMandatoryMessage(field)}></FormTreeView>
        break;
      case FormFieldType.TableSelect:
        content = <TableSelect field={field} selectedValues={this.state.saveData.formFieldValues[field.id]} onChange={(val) => this.onTextFieldValueChanged(field, val)}
          errorMessage={this.getMandatoryMessage(field)}></TableSelect>
        break;
      case FormFieldType.InfoText:
        let contentText = field.config ? field.config.text : "";
        content = <div className={styles.infoText} dangerouslySetInnerHTML={{ __html: contentText }}></div>;
        break;
    }

    return (
      <div key={field.id}>
        {content}
      </div>
    )
  }

  private getMandatoryMessage = (field: FormField): string | undefined => {
    if (!this.state.shouldValidate) {
      return undefined;
    }

    if (!this.isMandatoryFieldFilled(field)) {
      return field.mandatoryErrorMessage ? field.mandatoryErrorMessage : "Dieses Feld ist ein Pflichtfeld.";
    }

    return undefined;
  }

  private isMandatoryFieldFilled = (field: FormField): boolean => {
    if (!field.required) {
      return true;
    }

    if (!this.state.saveData.formFieldValues[field.id]) {
      return false;
    }

    if (field.type === FormFieldType.MultiSelect || field.type === FormFieldType.Treeview) {
      try {
        let values = JSON.parse(this.state.saveData.formFieldValues[field.id]);
        return values.length > 0;
      } catch (e) {
        return false;
      }
    }

    return true;
  }

  private onTextFieldValueChanged = (field: FormField, value: string | undefined): void => {
    value = value || "";

    let saveData = { ...this.state.saveData };
    saveData.formFieldValues[field.id] = value;
    this.setState({
      saveData: saveData
    }, () => {
      this.evaluateRules();
    })
  }

  private onCaptchaValue = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value: string | undefined): void => {
    this.setState({
      captchaValue: value || ""
    });
  }

  private onSave = async (): Promise<void> => {
    if (!this.validateRequiredField()) {
      this.setState({
        shouldValidate: true,
        saveError: false
      });
      return;
    }

    if(!this.captchaData) {
      return;
    }

    if(this.state.formData?.needsCaptcha && !this.state.captchaValue) {
      this.setState({
        shouldValidate: true
      });
      return;
    }

    try {
      this.setState({
        isLoading: true,
        saveError: false
      });

      const urlParams = new URLSearchParams(window.location.search);
      await FormServiceFactory.getFormService().saveFormData(FormConstants.SignUpFormId, urlParams.get("token") || "", this.state.saveData, this.captchaData, this.state.captchaValue);

      this.setState({
        formWasSent: true,
        isLoading: false
      })
    } catch (e) {
      console.error(e);

      this.setState({
        isLoading: false,
        saveError: true
      });
    }
  }

  private validateRequiredField = (): boolean => {
    if (!this.state.formData) {
      this.setState({
        shouldValidate: true
      });
      return false;
    }

    let currentSection = this.state.formData.sections[this.state.currentFormStep];
    for (let curField of currentSection.fields) {
      if (!this.isMandatoryFieldFilled(curField)) {
        return false;
      }
    }

    return true;
  }

  private dismissSaveError = (): void => {
    this.setState({
      saveError: false
    });
  }

  private evaluateRules = (): void => {
    if(!this.state.formData) {
      return;
    }

    let changedFormData = { ...this.state.formData };
    let anyChange = false;
    for(let curSection of changedFormData.sections) {
      for(let curField of curSection.fields) {
        if(!curField.rules) {
          continue;
        }

        for(let curRule of curField.rules) {
          let targetFieldValue = this.state.saveData.formFieldValues[curRule.fieldName] ? this.state.saveData.formFieldValues[curRule.fieldName] : "";
          let ruleIsTrue = false;
          if(curRule.notContainsValue) {
            ruleIsTrue = !targetFieldValue.toLowerCase().includes(curRule.notContainsValue.toLowerCase());
          } else {
            ruleIsTrue = targetFieldValue.toLowerCase().includes(curRule.containsValue.toLowerCase());
          }

          if(curRule.setToRequiredIfTrue !== null) {
            let isRequired = !curRule.setToRequiredIfTrue;
            if(ruleIsTrue) {
              isRequired = !isRequired;
            }

            if(isRequired !== curField.required) {
              curField.required = isRequired;
              anyChange = true;
            }
          }
          if(curRule.showIfTrue !== null) {
            curField.hide = !ruleIsTrue;
            anyChange = true;
          }
        }
      }
    }

    if(anyChange) {
      this.setState({
        formData: changedFormData
      });
    }
  }
}